import { createFileRoute } from '@tanstack/react-router';
import { Clock, Envelope } from '@phosphor-icons/react';
import { FieldGrid, SettingCard, SettingRow } from '../../../../../components/settings/setting-card';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { Select } from '../../../../../components/ui/select';
import { Toggle } from '../../../../../components/ui/toggle';
import type { AlertChannels, StaffNotificationPrefs } from '@mybusinessmate/schema';
import { Skeleton } from '../../../../../components/ui/skeleton';
import { ALERTS, CLOCK_OPTIONS, formatClock } from '../../../../../lib/staff-prefs';
import { useStaffPreferences } from '../../../../../lib/workspace-api';

export const Route = createFileRoute('/subdomain/admin/_staff/settings/notifications')({
  component: NotificationsPage,
});

const CHANNELS: { key: keyof AlertChannels; label: string }[] = [
  { key: 'inApp', label: 'In-app' },
  { key: 'email', label: 'Email' },
  { key: 'push', label: 'Push' },
];

const QUIET_DAYS: { value: StaffNotificationPrefs['quietHours']['days']; label: string }[] = [
  { value: 'every-day', label: 'Every day' },
  { value: 'weekdays', label: 'Weekdays' },
  { value: 'weekends', label: 'Weekends' },
];

function NotificationsPage() {
  const { data: prefs, save } = useStaffPreferences('notifications');
  if (!prefs) return <Skeleton className="h-(--mbm-layout-dialog-wide-width) rounded-(--mbm-radius-lg)" />;
  const update = (next: StaffNotificationPrefs) => save.mutate(next);

  const setQuiet = (patch: Partial<StaffNotificationPrefs['quietHours']>) =>
    update({ ...prefs, quietHours: { ...prefs.quietHours, ...patch } });
  const quiet = prefs.quietHours;

  return (
    <>
      <SettingCard title="Delivery" subtitle="How and when alerts reach you.">
        <SettingRow
          icon={Envelope}
          title="Daily digest email"
          subtitle="One email at 9:00 AM with everything from yesterday."
          trailing={
            <Toggle
              aria-label="Daily digest email"
              checked={prefs.dailyDigest}
              onCheckedChange={(on) => update({ ...prefs, dailyDigest: on })}
            />
          }
        />
        <SettingRow
          icon={Clock}
          title="Quiet hours"
          subtitle={`Pause email and push from ${formatClock(quiet.from)} to ${formatClock(quiet.to)}. Urgent alerts still come through.`}
          trailing={<Toggle aria-label="Quiet hours" checked={quiet.on} onCheckedChange={(on) => setQuiet({ on })} />}
        />
        {quiet.on ? (
          <FieldGrid className="pb-(--mbm-space-2) pl-(--mbm-space-9) sm:grid-cols-3">
            <Select
              variant="filled"
              label="From"
              value={quiet.from}
              onChange={(e) => setQuiet({ from: e.target.value })}
            >
              {CLOCK_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
            <Select variant="filled" label="To" value={quiet.to} onChange={(e) => setQuiet({ to: e.target.value })}>
              {CLOCK_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
            <Select
              variant="filled"
              label="Days"
              value={quiet.days}
              onChange={(e) => setQuiet({ days: e.target.value as StaffNotificationPrefs['quietHours']['days'] })}
            >
              {QUIET_DAYS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </Select>
          </FieldGrid>
        ) : null}
      </SettingCard>

      <SettingCard
        title="What you hear about"
        subtitle="Pick where each alert goes. Push goes to the MyBusinessMate app on your phone."
      >
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="text-label-small text-text-section uppercase">
              <th scope="col" className="py-(--mbm-space-3) pr-(--mbm-space-4) font-semibold">
                Alert
              </th>
              {CHANNELS.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  className="w-(--mbm-size-list-row-tall) py-(--mbm-space-3) text-center font-semibold"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ALERTS.map((alert) => (
              <tr key={alert.id} className="border-t border-border-line">
                <th scope="row" className="py-(--mbm-space-3) pr-(--mbm-space-4) text-left font-normal">
                  <span className="block text-label-medium text-text-primary">{alert.title}</span>
                  {'hint' in alert && alert.hint ? (
                    <span className="block text-body-xsmall text-text-tertiary">{alert.hint}</span>
                  ) : null}
                </th>
                {CHANNELS.map((c) => (
                  <td key={c.key} className="py-(--mbm-space-3) text-center">
                    <Checkbox
                      containerClassName="flex justify-center"
                      aria-label={`${alert.title} · ${c.label}`}
                      checked={prefs.alerts[alert.id][c.key]}
                      disabled={alert.locked}
                      onChange={(e) =>
                        update({
                          ...prefs,
                          alerts: {
                            ...prefs.alerts,
                            [alert.id]: { ...prefs.alerts[alert.id], [c.key]: e.target.checked },
                          },
                        })
                      }
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </SettingCard>
    </>
  );
}
